/**
 * API do bot (spec 022): o fluxo publicado no motor da Zeepchat lê e escreve
 * no ERP — não no TiFlux nem no worker do disparo.
 *
 * Medido em 28/09 no fluxo convertido do Blip: o bot consultava dois lugares
 * fora do ERP (o worker, sem autenticação, para achar o ticket do telefone; o
 * TiFlux, para ler a etapa) e as 6 escritas estavam simuladas. O ERP já tinha
 * tudo o que o bot lê — o espelho dos tickets, os telefones extraídos da ficha
 * (spec 012) e a ficha legível.
 *
 * A credencial é uma chave POR TENANT, com escopo mínimo, mostrada UMA vez e
 * guardada só como SHA-256. O tenant sai da chave; `X-Tenant` é ignorado.
 */
import { z } from 'zod';
import { Dateish } from './common';
import { DeskOriginSchema } from './desks';
import { StageSituationSchema } from './situations';

// ── A chave ─────────────────────────────────────────────────────────────────
export const BOT_API_KEY_PREFIX = 'erpbot_';
/** caracteres aleatórios depois do prefixo (base62, CSPRNG) — ~381 bits */
export const BOT_API_KEY_RANDOM_LENGTH = 64;
/** formato exato: o que não casa nem chega ao banco */
export const BOT_API_KEY_PATTERN = /^erpbot_[0-9A-Za-z]{64}$/;

export const BOT_SCOPES = ['tickets:read', 'tickets:note', 'tickets:stage'] as const;
export const BotScopeSchema = z.enum(BOT_SCOPES);
export type BotScope = z.infer<typeof BotScopeSchema>;

export const BOT_SCOPE_LABELS: Record<BotScope, string> = {
  'tickets:read': 'Ler tickets',
  'tickets:note': 'Registrar nota do bot',
  'tickets:stage': 'Mudar a etapa',
};

export const BOT_SCOPE_HINTS: Record<BotScope, string> = {
  'tickets:read': 'busca por telefone, leitura do ticket e a lista dos clientes operados no ERP',
  'tickets:note': 'nota interna na linha do tempo — vale também em ticket espelhado',
  'tickets:stage': 'só em ticket operado no ERP (erpOnly); em ticket espelhado responde 409',
};

export const BOT_API_KEY_NAME_MAX = 80;

/** POST /bot-keys (admin) */
export const BotApiKeyCreateBodySchema = z
  .object({
    name: z.string().trim().min(1).max(BOT_API_KEY_NAME_MAX),
    // repetido não conta duas vezes: o banco guarda o conjunto
    scopes: z
      .array(BotScopeSchema)
      .min(1)
      .transform((scopes) => BOT_SCOPES.filter((s) => scopes.includes(s))),
  })
  .strict();
export type BotApiKeyCreateBody = z.input<typeof BotApiKeyCreateBodySchema>;

const PersonRefSchema = z.object({ id: z.number().int(), name: z.string() });

/**
 * A chave como a tela a lista: nunca o valor. `fingerprint` são os 8 primeiros
 * hex do SHA-256 guardado — quem tem a chave confere qual é sem mostrá-la:
 * `printf '%s' "$CHAVE" | sha256sum | cut -c1-8`.
 */
export const BotApiKeySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  scopes: z.array(BotScopeSchema),
  fingerprint: z.string(),
  createdBy: PersonRefSchema.nullable(),
  createdAt: Dateish,
  /** null = nunca usada */
  lastUsedAt: Dateish.nullable(),
  /** null = ativa */
  revokedAt: Dateish.nullable(),
  revokedBy: PersonRefSchema.nullable(),
});
export type BotApiKey = z.infer<typeof BotApiKeySchema>;

/** Resposta da criação — a ÚNICA vez em que a chave sai do servidor. */
export const BotApiKeyCreatedSchema = z.object({
  key: z.string(),
  apiKey: BotApiKeySchema,
});
export type BotApiKeyCreated = z.infer<typeof BotApiKeyCreatedSchema>;

// ── As rotas do bot: as quatro do ticket (spec 022) e a lista dos operados ──
/**
 * GET /bot/tickets?phone=<dígitos>&status=open|all[&include=client_contacts]
 *
 * O contato do CLIENTE fica fora por padrão: na PoolTrack o cliente é a
 * associação, e um funcionário dela que escrevesse para o bot receberia o
 * ticket de algum associado. Só entra quando o fluxo pede.
 *
 * `phone` que não é telefone, ou que é um número de ENCHIMENTO (isFillerPhone:
 * a lista de números de mentira, com ou sem o 9 — inclusive o solicitante fixo
 * dos tickets do worker da PoolTrack), é 422 `invalid_phone`, nunca lista
 * vazia: vazia quer dizer "sem ticket" (revisão da spec 025, achado do F-79).
 */
export const BotTicketListQuerySchema = z.object({
  phone: z.string().trim().min(1).max(40),
  status: z.enum(['open', 'all']).default('open'),
  include: z.literal('client_contacts').optional(),
});
export type BotTicketListQuery = z.infer<typeof BotTicketListQuerySchema>;

/**
 * Por onde o telefone casou — o mais forte, quando casa por mais de um: a
 * ficha (TELEFONE / TELEFONE ADICIONAL da descrição), o solicitante do ticket
 * ou o contato do cliente (só com `include=client_contacts`).
 */
export const BOT_TICKET_MATCH_SOURCES = ['sheet', 'requester', 'client_contact'] as const;
export const BotTicketMatchSourceSchema = z.enum(BOT_TICKET_MATCH_SOURCES);
export type BotTicketMatchSource = z.infer<typeof BotTicketMatchSourceSchema>;

/** teto da lista por telefone — o mais recente primeiro */
export const BOT_TICKET_LIST_LIMIT = 50;

export const BOT_TICKET_STATUSES = ['open', 'closed', 'canceled'] as const;

export const BotTicketItemSchema = z.object({
  ticketNumber: z.number().int(),
  title: z.string(),
  /** aberto, fechado ou cancelado — o mesmo corte do `status=open|all` */
  status: z.enum(BOT_TICKET_STATUSES),
  desk: z.object({ id: z.number().int(), name: z.string(), origin: DeskOriginSchema }),
  /**
   * O ticket é operado SÓ no ERP (spec 024): true para ticket de mesa `erp` e
   * para ticket fora do espelho (mesmo numa mesa `tiflux`); false para ticket
   * espelhado do TiFlux. É a negação do predicado único de "espelhado" — a
   * `desk.origin` sozinha não diz mais. A Blip o consulta para decidir se
   * encaminha o cliente ao bot do ERP; a etapa só muda (`/stage`) quando é true.
   */
  erpOnly: z.boolean(),
  stage: z.object({
    id: z.number().int(),
    name: z.string(),
    situation: StageSituationSchema,
    /**
     * id da etapa no TiFlux (vínculo do espelho) — o fluxo convertido compara
     * como comparava. null quando a etapa não veio do TiFlux.
     */
    externalId: z.number().int().nullable(),
  }),
  createdAt: Dateish,
  updatedAt: Dateish,
  /** o identificador do recurso no perfil do tenant (o mesmo rótulo da lista) */
  resourceId: z.string().nullable(),
  /** quando o disparo por ticket (spec 014) mandou a mensagem; null = nunca */
  lastDispatchAt: Dateish.nullable(),
});
export type BotTicketItem = z.infer<typeof BotTicketItemSchema>;

/** item da busca por telefone: o ticket + por onde o telefone casou */
export const BotTicketMatchSchema = BotTicketItemSchema.extend({ matchedBy: BotTicketMatchSourceSchema });
export type BotTicketMatch = z.infer<typeof BotTicketMatchSchema>;

/** a ficha em pares chave → valor, com as chaves do parser (parseSgrDescription) */
export const BotTicketSheetSchema = z.record(z.string(), z.union([z.string(), z.array(z.string())]));
export type BotTicketSheet = z.infer<typeof BotTicketSheetSchema>;

/** GET /bot/tickets/:number */
export const BotTicketDetailSchema = BotTicketItemSchema.extend({
  /** null quando a descrição não tem ficha reconhecível */
  sheet: BotTicketSheetSchema.nullable(),
  /** a descrição em texto puro (sem HTML) */
  description: z.string(),
});
export type BotTicketDetail = z.infer<typeof BotTicketDetailSchema>;

export const BOT_NOTE_MAX_LENGTH = 4000;
/** autor da nota do bot na linha do tempo */
export const BOT_NOTE_AUTHOR_NAME = 'Bot';

/**
 * Conta como o Postgres conta (`char_length`): um emoji é UM caractere — o
 * `length` do JavaScript contaria dois.
 */
export const botNoteLength = (text: string): number => Array.from(text).length;

/** POST /bot/tickets/:number/notes */
export const BotNoteBodySchema = z
  .object({
    text: z
      .string()
      .trim()
      .min(1)
      .refine((text) => botNoteLength(text) <= BOT_NOTE_MAX_LENGTH, {
        message: `a nota tem no máximo ${BOT_NOTE_MAX_LENGTH} caracteres`,
      })
      // o Postgres não guarda NUL em text: sem isto seria 500, não 422
      .refine((text) => !text.includes(String.fromCharCode(0)), { message: 'a nota não pode ter o caractere nulo' }),
  })
  .strict();
export type BotNoteBody = z.infer<typeof BotNoteBodySchema>;

export const BotNoteCreatedSchema = z.object({
  id: z.number().int(),
  ticketNumber: z.number().int(),
  createdAt: Dateish,
});
export type BotNoteCreated = z.infer<typeof BotNoteCreatedSchema>;

/** POST /bot/tickets/:number/stage — exatamente UM dos dois */
export const BotStageBodySchema = z.union([
  z.object({ stageId: z.number().int().positive().max(2_147_483_647) }).strict(),
  z.object({ externalStageId: z.number().int().positive().max(Number.MAX_SAFE_INTEGER) }).strict(),
]);
export type BotStageBody = z.infer<typeof BotStageBodySchema>;

/**
 * GET /bot/erp-clients (escopo `tickets:read`) — a lista ÚNICA de "clientes no
 * caminho novo" (revisão da spec 025, D6): os clientes operados no ERP do tenant
 * da chave, lidos do registro do desligamento (spec 024,
 * `tiflux_client_detachments`). É o que o worker da PoolTrack consome no cron do
 * HSM, no script de campanha e na marcação, em vez de um array fixo no código.
 *
 * CONTRATO com o worker — não muda sem avisar do outro lado:
 * - `tifluxClientIds`: o id do cliente NO TIFLUX gravado no desligamento. Só
 *   entra quem tem um: o operado sem vínculo com o TiFlux não tem o que excluir
 *   lá (mas está em `localClientIds`);
 * - `localClientIds`: o id no ERP de TODOS os operados;
 * - `generatedAt`: ISO 8601 (UTC) de quando a lista foi lida.
 * As listas vêm sem repetição e em ordem crescente. Lista vazia é resposta
 * válida (nenhum cliente operado); status que não seja 200 NÃO é lista vazia.
 */
export const BotErpClientsSchema = z
  .object({
    tifluxClientIds: z.array(z.number().int()),
    localClientIds: z.array(z.number().int()),
    generatedAt: z.string(),
  })
  .strict();
export type BotErpClients = z.infer<typeof BotErpClientsSchema>;

/**
 * Quem escreveu pelo bot, no payload do evento do ticket (auditoria): a chave
 * pelo NOME, e o id para achar a linha mesmo depois de renomeada.
 */
export const BotEventActorSchema = z.object({ keyId: z.number().int(), keyName: z.string() });
export type BotEventActor = z.infer<typeof BotEventActorSchema>;

/** lê o `bot` do payload de um evento de ticket, se houver */
export function botActorOf(payload: Record<string, unknown> | null | undefined): BotEventActor | null {
  const parsed = BotEventActorSchema.safeParse(payload?.bot);
  return parsed.success ? parsed.data : null;
}
